
import React, { useState, useEffect } from 'react';
import { Users, Shield, ShieldCheck, Search, Loader2, UserPlus, Trash2, Database, AlertCircle, CheckCircle2, Sparkles, Clock } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { UserRole, MysterySponsorship } from '../types';
import { fetchPendingSponsorships, confirmMysterySponsorship } from '../services/sponsorshipService';

interface UserProfile {
  id: string;
  email: string | null;
  username: string | null;
  role: string | null;
  created_at: string;
}

const AdminView: React.FC = () => {
  const [dataAgents, setDataAgents] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Community Mystery Box sponsorships awaiting payment confirmation (manual settlement
  // until the KHQR webhook is wired). Confirming mints the prize into the box.
  const [pendingSponsorships, setPendingSponsorships] = useState<MysterySponsorship[]>([]);
  const [confirming, setConfirming] = useState<string | null>(null);

  const loadPendingSponsorships = async () => {
    try { setPendingSponsorships(await fetchPendingSponsorships()); }
    catch (e) { console.error('Failed to load sponsorships', e); }
  };

  const handleConfirmSponsorship = async (id: string) => {
    setConfirming(id);
    try { await confirmMysterySponsorship(id, 'MANUAL'); await loadPendingSponsorships(); }
    catch (e) { console.error(e); }
    finally { setConfirming(null); }
  };

  useEffect(() => {
    fetchDataEntryTeam();
    loadPendingSponsorships();
  }, []);

  const fetchDataEntryTeam = async () => {
    setLoading(true);
    try {
      // Fetch only Data Agents (Data Entry Specialists)
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('role', UserRole.DATA_AGENT)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setDataAgents(data || []);
    } catch (e) {
      console.error("Failed to fetch team", e);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!emailInput.trim()) return;
    
    // Strict Gmail check suggestion (though system allows others, we warn for best practice if requested)
    if (!emailInput.includes('@gmail.com')) {
        const confirm = window.confirm("This doesn't look like a standard Gmail address. Proceed anyway?");
        if (!confirm) return;
    }

    setProcessing(true);
    setStatusMsg(null);

    try {
      // 1. Find user by email (case-insensitive)
      const { data: users, error: searchError } = await supabase
        .from('users')
        .select('*')
        .ilike('email', emailInput.trim());

      if (searchError) throw searchError;

      if (!users || users.length === 0) {
        setStatusMsg({
          type: 'error',
          text: 'User not found. Please ask them to sign in to Tinaney via Google first.'
        });
        setProcessing(false);
        return;
      }

      const targetUser = users[0];

      // 2. Prevent modifying other CTOs
      if (targetUser.role === UserRole.CTO) {
        setStatusMsg({ type: 'error', text: 'Cannot modify a Super Admin account.' });
        setProcessing(false);
        return;
      }

      // 3. Update Role to DATA_AGENT (Used for Data Entry)
      // IMPORTANT: We use .select() to verify the update actually succeeded. 
      // If RLS blocks it, no error is thrown but no data is returned.
      const { data: updatedData, error: updateError } = await supabase
        .from('users')
        .update({ role: UserRole.DATA_AGENT })
        .eq('id', targetUser.id)
        .select();

      if (updateError) throw updateError;
      
      if (!updatedData || updatedData.length === 0) {
          throw new Error("Update failed. You may not have permission (RLS) to modify this user. Please check 'Database Setup > RLS Policies'.");
      }

      setStatusMsg({ type: 'success', text: `Data Entry access granted to ${targetUser.email}` });
      setEmailInput('');
      fetchDataEntryTeam(); // Refresh list

    } catch (e: any) {
      console.error("Grant failed", e);
      setStatusMsg({ type: 'error', text: e.message || "Failed to grant access." });
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeAccess = async (userId: string) => {
    if (!window.confirm("Revoke Data Entry permissions for this user? They will return to General User status.")) return;
    
    try {
      const { data, error } = await supabase
        .from('users')
        .update({ role: UserRole.GENERAL_USER })
        .eq('id', userId)
        .select();

      if (error) throw error;
      
      if (!data || data.length === 0) {
          throw new Error("Update blocked by RLS policies.");
      }

      // Optimistic update
      setDataAgents(prev => prev.filter(u => u.id !== userId));
    } catch (e: any) {
      alert(`Failed to revoke access: ${e.message}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      <div className="border-b border-slate-700 pb-6">
        <h1 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-3">
          <Shield className="text-indigo-500" />
          Data Entry Management
        </h1>
        <p className="text-slate-400">
          Authorize personnel to input Cambodia Public Data into the Tinaney Ledger.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        
        {/* LEFT COLUMN: ADD USER */}
        <div className="md:col-span-1 space-y-6">
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-lg">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <UserPlus size={20} className="text-emerald-400" /> Add Data Entry User
            </h3>
            <p className="text-xs text-slate-400 mb-4">
              Enter the <strong>Gmail address</strong> of the user you wish to authorize.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Gmail Address</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input 
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="user@gmail.com" 
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-9 pr-3 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleGrantAccess()}
                  />
                </div>
              </div>

              {statusMsg && (
                <div className={`text-xs p-3 rounded border flex items-start gap-2 ${
                  statusMsg.type === 'success' ? 'bg-emerald-900/20 border-emerald-900/50 text-emerald-400' : 'bg-red-900/20 border-red-900/50 text-red-400'
                }`}>
                  {statusMsg.type === 'success' ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <AlertCircle size={14} className="shrink-0 mt-0.5" />}
                  <span>{statusMsg.text}</span>
                </div>
              )}

              <button 
                onClick={handleGrantAccess}
                disabled={!emailInput || processing}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-lg font-bold shadow-lg shadow-emerald-900/20 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
                Authorize User
              </button>
            </div>
          </div>

          <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800 text-xs text-slate-500">
             <strong>Requirement:</strong> Users must have an existing Tinaney account (signed in via Google) before they can be promoted to the Data Entry role.
          </div>
        </div>

        {/* RIGHT COLUMN: TEAM LIST */}
        <div className="md:col-span-2">
           <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl overflow-hidden min-h-[400px]">
             <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-800/50">
                <div className="flex items-center gap-2">
                  <Database size={18} className="text-indigo-400" />
                  <h3 className="font-bold text-white">Data Entry Team</h3>
                </div>
                <span className="text-xs bg-indigo-500/10 text-indigo-400 px-2 py-1 rounded-full border border-indigo-500/20">
                  {dataAgents.length} Active
                </span>
             </div>

             {loading ? (
                <div className="flex justify-center items-center h-64">
                   <Loader2 className="animate-spin text-indigo-500" size={32} />
                </div>
             ) : dataAgents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                   <Users size={48} className="mb-4 opacity-20" />
                   <p>No Data Entry specialists assigned yet.</p>
                </div>
             ) : (
                <div className="overflow-x-auto">
                   <table className="w-full text-left text-sm">
                      <thead className="bg-slate-900/80 text-slate-400 uppercase font-semibold text-[10px] tracking-wider">
                         <tr>
                            <th className="px-6 py-3">User</th>
                            <th className="px-6 py-3">Role</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                         </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                         {dataAgents.map(user => (
                            <tr key={user.id} className="hover:bg-slate-700/30 transition-colors">
                               <td className="px-6 py-4">
                                  <div className="font-medium text-white">{user.username || 'Unknown'}</div>
                                  <div className="text-slate-400 text-xs">{user.email}</div>
                               </td>
                               <td className="px-6 py-4 text-emerald-400 text-xs font-mono">
                                  DATA_ENTRY
                               </td>
                               <td className="px-6 py-4 text-right">
                                  <button 
                                     onClick={() => handleRevokeAccess(user.id)}
                                     className="text-slate-500 hover:text-red-400 hover:bg-red-900/10 px-3 py-1.5 rounded transition-colors text-xs flex items-center gap-1 ml-auto"
                                     title="Revoke Data Entry Access"
                                  >
                                     <Trash2 size={14} /> Revoke
                                  </button>
                               </td>
                            </tr>
                         ))}
                      </tbody>
                   </table>
                </div>
             )}
           </div>
        </div>

      </div>

      {/* Community Mystery Box — confirm sponsor payments (manual until KHQR webhook) */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg overflow-hidden">
        <div className="p-5 border-b border-slate-700 flex items-center gap-2">
          <Sparkles size={18} className="text-indigo-400" />
          <h3 className="text-white font-bold">Community Box · Pending Payments</h3>
          <span className="ml-auto text-xs text-slate-500">{pendingSponsorships.length} awaiting</span>
        </div>
        <div className="p-5">
          {pendingSponsorships.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No sponsorships awaiting payment.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-500 mb-2">
                Confirm once payment (KHQR/card) clears — this drops the prize into the Community Mystery Box.
              </p>
              {pendingSponsorships.map(s => (
                <div key={s.id} className="flex items-center justify-between bg-slate-900/40 border border-slate-700 rounded-lg px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{s.reward_name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5"><Clock size={11} /> {s.amount} {s.currency} · {new Date(s.created_at).toLocaleDateString()}</p>
                  </div>
                  <button
                    onClick={() => handleConfirmSponsorship(s.id)}
                    disabled={confirming === s.id}
                    className="flex items-center gap-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0"
                  >
                    {confirming === s.id ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />} Confirm payment
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default AdminView;
